import { LAST_UPDATED } from './legalMeta'

/**
 * Cancellation & Refund Policy.
 *
 * The cut-offs here are the ones the product actually enforces, not a generic
 * 24-hour rule:
 *  - Consultations: `canCancel` / `canReschedule` are true until the booking is
 *    `completed | ready_to_start | in_progress | cancelled`
 *    (`apps/astrology/src/components/sections/bookingDetails/bookingDetailsData.js`).
 *  - Shop orders: self-cancel is offered only while the API reports
 *    `can_cancel`, i.e. before the order is handed to Shiprocket
 *    (`apps/shop/src/components/orders/CancelOrderModal.jsx`).
 *  - Poojas: full refund until the sankalp is taken
 *    (`apps/shop/src/components/pooja/PoojaFaqSection.jsx`).
 *  - Timeline wording matches the "5-7 working days" the order and booking
 *    pages already tell customers.
 */
export const REFUND_POLICY = {
  slug: 'refund-policy',
  badge: 'Legal',
  title: 'Cancellation & Refund Policy',
  intro:
    'Book with confidence. This policy sets out, service by service, when you can cancel, when you can reschedule, what is refundable, and how long the money takes to come back.',
  updated: LAST_UPDATED,
  summary: [
    'Consultations — cancel or reschedule yourself for a full refund any time before the session is marked ready to start.',
    'Temple poojas — full refund any time before the sankalp is taken in your name; not refundable once it has been recited.',
    'Courses — full refund if you cancel within 7 days of enrolling and have not watched more than 15% of the lessons or attended a live class.',
    'Products — cancel free before dispatch; return an unused item in its original packing within 7 days of delivery.',
    'Approved refunds go back to the method you paid with, usually in 5-7 working days.',
  ],
  sections: [
    {
      heading: '1. Scope of this policy',
      body: [
        'This policy applies to everything bought from {company} online — consultations and gemstone sessions on our jyotish portal, temple pooja (seva) bookings, academy courses and live classes, and products from our shop.',
        'It forms part of our Terms of Service. Shipping timelines, delivery attempts and undelivered parcels are dealt with in our Shipping & Delivery Policy. Nothing in this policy limits the rights you have under the Consumer Protection Act, 2019 — if what you received was defective, damaged, spurious or not as described, those rights apply regardless of the timelines below.',
      ],
    },
    {
      heading: '2. Consultations, readings and gemstone sessions',
      subsections: [
        {
          heading: '2.1 Cancelling or rescheduling yourself',
          body: [
            'Open your booking from your dashboard and use Cancel or Reschedule. Both options stay available for as long as the booking is open — that is, until the session is marked ready to start, is in progress, or has already been completed or cancelled. A cancellation made while the booking is still open is refunded in full.',
            'Rescheduling is free and does not cost you the booking. The new slot is subject to the astrologer’s availability.',
          ],
        },
        {
          heading: '2.2 Once the session has started or passed',
          list: [
            'If you do not join and did not cancel while you could, the slot is treated as used and the fee is not refundable — the astrologer held that time for you.',
            'If you join late, the session still ends at its scheduled finish time and the fee stands.',
            'If a session had to be cut short because of a problem at our end or the astrologer’s end, we will complete it in a follow-up session at no charge, or refund it.',
          ],
        },
        {
          heading: '2.3 If we or the astrologer cannot attend',
          body: [
            'If an astrologer has to cancel or cannot attend, you get the choice of the next available slot, a different astrologer of the same standing, or a full refund. The same applies if a technical failure on our side prevented the session from happening at all.',
          ],
        },
        {
          heading: '2.4 Delivered readings and reports',
          body: [
            'A reading, report, remedy list or chart that has already been prepared and delivered to you cannot be returned, and the fee for it is not refundable, because the work has been done and cannot be taken back. If you believe the output does not match what was promised on the booking page, raise it with us{contact} within 7 days and we will review it and, where we agree, redo the work or refund it.',
          ],
        },
      ],
      note: 'We do not refund a consultation because a prediction did not come true, because you disagreed with the guidance, or because an outcome you hoped for did not happen. You are paying for the astrologer’s time and expertise, not for a guaranteed result. Our Disclaimer explains this.',
    },
    {
      heading: '3. Temple pooja (seva) bookings',
      subsections: [
        {
          heading: '3.1 Cancelling before the sankalp',
          body: [
            'You may cancel a pooja booking at any time before the sankalp is taken in your name, and we refund it in full to the method you paid with. Write to us{contact} with your booking reference, or cancel from your dashboard where the option is shown.',
          ],
        },
        {
          heading: '3.2 Once the sankalp has been taken',
          body: [
            'Once Pandit Ji has recited the sankalp with your name and gotra, the seva has been offered on your behalf. It cannot be cancelled, reversed or refunded, in whole or in part, including where you later change your mind or cannot be reached.',
          ],
        },
        {
          heading: '3.3 If we cannot perform the seva',
          body: [
            'If the temple closes, the muhurat changes, the ritual cannot be performed, or we cannot arrange it for any reason on our side, we will offer you the next suitable date or a full refund, as you prefer.',
            'If a package promised a video of the ritual and we are unable to provide one, we will refund the part of the price attributable to it. If prasad that was part of your package cannot be sent, that part is refunded too.',
          ],
        },
      ],
    },
    {
      heading: '4. Academy courses and live classes',
      subsections: [
        {
          heading: '4.1 Cooling-off period',
          body: [
            'If a course is not what you expected, write to us{contact} within 7 days of enrolling and we will refund you in full, provided you have not watched more than 15% of the course lessons, attended a live class, downloaded the course material, or been issued a certificate. This is our goodwill window; please use it honestly.',
          ],
        },
        {
          heading: '4.2 After the cooling-off period',
          list: [
            'Once the 7 days have passed, or once you have crossed any of the thresholds above, the enrolment fee is not refundable. Digital course material cannot be returned once accessed.',
            'A paid instalment is not refundable after the cooling-off period, and clearing an instalment does not restart the window.',
            'If you stop attending, your enrolment stays open for the access period stated on the course page; there is no partial refund for lessons you did not watch.',
          ],
        },
        {
          heading: '4.3 Instalments and unpaid balances',
          body: [
            'Where you pay in parts, you still owe the full course price. An overdue instalment may lead us to pause your access until it is cleared. If you decide not to continue, write to us — we will not pursue the remaining instalments for a course you stop within the cooling-off window, and amounts already paid are dealt with by the rules above.',
          ],
        },
        {
          heading: '4.4 If we cancel or reschedule',
          body: [
            'If we cancel a course outright, you get a full refund of everything paid for it. If a live class is rescheduled, we will notify enrolled students; a rescheduled class is not a cancellation and does not create a refund. If a class we cannot hold at all is not made up or replaced with a recording, we will credit or refund the value of that class.',
          ],
        },
      ],
    },
    {
      heading: '5. Shop products',
      subsections: [
        {
          heading: '5.1 Cancelling before dispatch',
          body: [
            'You can cancel an order yourself from the order page for as long as the order has not been handed to the courier — the Cancel option disappears once it has. Cancelling a paid order refunds it in full, automatically, to the method you paid with. Where the Cancel option is no longer shown, write to us{contact} straight away and we will stop the parcel if it has not yet left.',
          ],
        },
        {
          heading: '5.2 Returning an item after delivery',
          body: [
            'You may return an eligible item within 7 days of delivery. To be accepted, it must be unused, unworn, undamaged and complete, in its original packaging with all tags, certificates, inserts and free gifts. Raise the return from your order page or write to us{contact} within those 7 days with your order number, the reason, and photographs where the item arrived damaged.',
            'Once we receive the item back and check its condition, we approve the refund or send a replacement, as you prefer. We may decline a return where the item comes back used, altered, incomplete or damaged in a way the courier scan does not explain.',
          ],
        },
        {
          heading: '5.3 What cannot be returned',
          list: [
            'Items personalised for you — anything engraved, strung, sized or made to your specification.',
            'Items energized, abhishek-performed or prayed over in your name before dispatch, because that work was done specifically for you.',
            'Prasad, and any consumable, edible or perishable item.',
            'Any item whose seal, certificate tag or protective packing has been opened or removed, where the listing said it must stay intact.',
            'Digital downloads and course material.',
          ],
        },
        {
          heading: '5.4 Damaged, wrong or missing items',
          body: [
            'If your parcel arrives damaged, if the wrong item was sent, or if something is missing, tell us within 48 hours of delivery and send photographs of the item and the outer packing. We will replace it or refund it in full, including the shipping you paid, and we will arrange and pay for the return pickup. This is in addition to your statutory rights.',
          ],
        },
        {
          heading: '5.5 Natural variation is not a defect',
          body: [
            'Gemstones, rudraksha, shaligram, wood, metal and other natural items vary in shade, grain, inclusion, veining and exact weight. A reasonable variation from the product photograph is normal and is not a ground for a free return. Where a listing states a certification, the accompanying certificate is the authoritative description of the item.',
          ],
        },
        {
          heading: '5.6 Who pays for return shipping',
          list: [
            'We pay — where the item was damaged, defective, wrong, incomplete or not as described.',
            'You pay — where you are returning an item because you changed your mind, ordered the wrong thing, or no longer want it. Where we arrange the pickup for your convenience, the pickup charge may be deducted from your refund, and we will tell you the amount before we proceed.',
            'The original shipping charge is refunded where the fault was ours, and not refunded on a change-of-mind return.',
          ],
        },
        {
          heading: '5.7 Refused and undelivered parcels',
          body: [
            'If you refuse a prepaid parcel at the door or it comes back to us undelivered after the courier attempts, we refund the item value and deduct the actual to-and-fro shipping cost. Repeatedly refusing cash-on-delivery parcels may mean we stop offering that option to your address. Our Shipping & Delivery Policy explains delivery attempts and what to do when a delivery fails.',
          ],
        },
      ],
    },
    {
      heading: '6. Payment problems and duplicate charges',
      list: [
        'If your payment failed but money left your account, it is usually reversed automatically by your bank within 5-7 working days without any action from you.',
        'If you were charged twice for one booking or order, or charged for something that never got confirmed, write to us{contact} with the payment reference. Once verified, we refund the full extra amount — these are never treated as a change-of-mind cancellation.',
        'Do not pay a second time if the first payment does not show up. Contact us instead.',
      ],
    },
    {
      heading: '7. How to request a refund',
      list: [
        'Where the product offers a self-service option — Cancel on an open booking or order — use it; those refunds start immediately.',
        'Otherwise write to us{contact} with your order or booking reference, what you are asking for, and photographs where the item arrived damaged or wrong.',
        'We acknowledge every request within 48 hours and give you a reference you can quote. We tell you our decision, with reasons, within 2 working days for a straightforward request, and we resolve the matter within one month at the outside.',
        'If you disagree with our decision, escalate it as set out on our Grievance Redressal page.',
      ],
    },
    {
      heading: '8. How refunds are paid, and how long they take',
      list: [
        'Refunds go back to the original payment method. We cannot redirect a refund to a different card, account or person.',
        'We initiate an approved refund within 2 working days of approval. It then typically reaches you in 5-7 working days, and may take up to 10 working days depending on your bank or card issuer.',
        'For a cash-on-delivery order, we refund by bank transfer or UPI to an account in the name of the person who placed the order, after confirming the details with you.',
        'Where only part of an order is returned, we refund the value of the returned items; any discount or offer applied to the order is recalculated against what you kept.',
        'Where goods were returned, the refund is processed after the item reaches us and passes inspection.',
        'Refunds are made in Indian Rupees. We do not reimburse a currency-conversion loss or a bank charge levied by your own bank.',
      ],
      note: 'We never ask for your card number, CVV, UPI PIN, OTP or net-banking password to process a refund, and we will never ask you to install an app or scan a QR code to receive one. Anyone doing so in our name is attempting fraud — stop and tell us{contact}.',
    },
    {
      heading: '9. When we may decline a refund',
      list: [
        'The cancellation window for that service had already closed, as set out above.',
        'The service was delivered as described, and the request is a change of mind after the fact.',
        'The item came back used, altered, incomplete, or without its certificate, tags or original packing.',
        'The request is based on an outcome not matching a prediction, which we never guarantee.',
        'The booking or order was made with false information, or we reasonably find the request to be fraudulent or an abuse of this policy.',
      ],
      body: [
        'Where we decline, we will tell you why in writing, and you can escalate the decision through our grievance process.',
      ],
    },
    {
      heading: '10. Contact us',
      body: [
        'For any cancellation, return or refund question, reach out to us{contact} with your reference number. We would rather fix the problem than keep the money — tell us what happened and we will look for a fair resolution.',
      ],
    },
  ],
}
